import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, Field, Mono, PageBody, PageHeader, Select } from "../ui/Field";

type Redirect = { id: string; source: string; target: string; status: number; sort_order: number };

export default function Redirects() {
  const { slug } = useParams();
  const [items, setItems] = useState<Redirect[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [status, setStatus] = useState<301 | 302>(301);

  const load = () => api.get<{ items: Redirect[] }>(`/api/sites/${slug}/redirects`).then((r) => setItems(r.items)).catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await api.post(`/api/sites/${slug}/redirects`, { source, target, status });
      setSource(""); setTarget(""); setStatus(301);
      load();
    } catch (e) { setErr((e as Error).message); }
  }
  async function remove(id: string) {
    if (!confirm("Remove redirect?")) return;
    try { await api.delete(`/api/sites/${slug}/redirects/${id}`); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 09 · Routing"
        title={<>Redirects</>}
        description={<>Permanent and temporary forwards emitted as <Mono>_redirects</Mono> at every publish. Splat patterns supported.</>}
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="R1" title="Add redirect" description="Source path → target path or URL.">
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_auto] gap-3 items-end">
          <Field label="Source" required value={source} onChange={(e) => setSource(e.target.value)} placeholder="/old-path" className="mb-0" />
          <Field label="Target" required value={target} onChange={(e) => setTarget(e.target.value)} placeholder="/new-path or https://…" className="mb-0" />
          <Select label="Status" value={status} onChange={(e) => setStatus(Number(e.target.value) as 301 | 302)} className="mb-0">
            <option value={301}>301 · Permanent</option>
            <option value={302}>302 · Temporary</option>
          </Select>
          <Button type="submit">Add</Button>
        </form>
        <p className="text-[12px] text-[var(--color-muted)] mt-3">
          Splat patterns supported, e.g. <Mono>/blog/*</Mono> → <Mono>/posts/:splat</Mono>.
        </p>
      </Card>

      <Card number="R2" title={`Existing (${items.length})`} className="mt-6" bodyClassName={items.length === 0 ? "p-5" : "p-0"}>
        {items.length === 0 ? (
          <EmptyState title="No redirects." description="Add one above to forward legacy URLs." />
        ) : (
          <table className="w-full text-[13.5px] border-collapse">
            <thead>
              <tr className="text-left label-tracked border-b border-[var(--color-rule)]">
                <th className="px-5 py-2.5">Source</th>
                <th className="px-5 py-2.5">Target</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-[var(--color-rule)] last:border-0">
                  <td className="px-5 py-2.5"><Mono>{r.source}</Mono></td>
                  <td className="px-5 py-2.5"><Mono>{r.target}</Mono></td>
                  <td className="px-5 py-2.5"><Badge tone={r.status === 301 ? "neutral" : "info"}>{r.status}</Badge></td>
                  <td className="px-5 py-2.5 text-right"><Button variant="ghost" size="sm" onClick={() => remove(r.id)}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </PageBody>
  );
}
